import React, { ReactNode, useEffect } from 'react';
import './ListTemplate.css';
import { useOptionalAppHeader } from '../../../layouts/AppHeaderContext';
import type { AppHeaderConfig } from '../../../layouts/AppHeaderContext';

export interface ListTemplateProps<T = any> {
  /**
   * Items to display in the list
   */
  items: T[];

  /**
   * Render function for each item
   */
  renderItem: (item: T, index: number) => ReactNode;

  /**
   * Optional header content (title, actions, etc.)
   */
  header?: ReactNode;

  /**
   * Optional search/filter component
   */
  search?: ReactNode;

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Empty state when no items
   */
  emptyState?: ReactNode;

  /**
   * Error state
   */
  error?: string | null;

  /**
   * Optional footer content (pagination, load more, etc.)
   */
  footer?: ReactNode;

  /**
   * Item key extractor
   */
  keyExtractor?: (item: T, index: number) => string | number;

  /**
   * Custom container class name
   */
  className?: string;

  /**
   * Custom list class name
   */
  listClassName?: string;

  /**
   * Enable item selection
   */
  selectable?: boolean;

  /**
   * Selected item IDs (for controlled selection)
   */
  selectedIds?: Set<string | number>;

  /**
   * Selection change handler
   */
  onSelectionChange?: (selectedIds: Set<string | number>) => void;

  /**
   * Bulk actions when items are selected
   */
  bulkActions?: ReactNode;

  /**
   * Custom loading component
   */
  loadingComponent?: ReactNode;

  /**
   * Item click handler
   */
  onItemClick?: (item: T, index: number) => void;

  /**
   * List variant
   */
  variant?: 'default' | 'compact' | 'comfortable';

  /**
   * AppHeader configuration to use instead of inline header
   */
  appHeader?: AppHeaderConfig;

  /**
   * Use AppHeader when appHeader is provided
   */
  useAppHeader?: boolean;

  /**
   * Allow internal vertical scrolling for the list content
   */
  scrollable?: boolean;
}

/**
 * ListTemplate organism
 *
 * Reusable template for list views with common patterns:
 * - Header with actions
 * - Search/filter capability
 * - Loading and empty states
 * - Item selection
 * - Bulk actions
 * - Pagination/footer
 *
 * @example
 * ```tsx
 * <ListTemplate
 *   items={users}
 *   renderItem={(user) => (
 *     <div className="user-item">
 *       <Avatar src={user.avatar} />
 *       <span>{user.name}</span>
 *     </div>
 *   )}
 *   header={
 *     <div className="list-header">
 *       <h2>Users</h2>
 *       <Button onClick={addUser}>Add User</Button>
 *     </div>
 *   }
 *   search={<SearchBar onSearch={handleSearch} />}
 *   loading={isLoading}
 *   emptyState={<EmptyState message="No users found" />}
 *   footer={<Pagination {...paginationProps} />}
 * />
 * ```
 */
export function ListTemplate<T = any>({
  items,
  renderItem,
  header,
  search,
  loading = false,
  emptyState,
  error,
  footer,
  keyExtractor = (_, index) => index,
  className = '',
  listClassName = '',
  selectable = false,
  selectedIds,
  onSelectionChange,
  bulkActions,
  loadingComponent,
  onItemClick,
  variant = 'default',
  appHeader,
  useAppHeader = false,
  scrollable = true,
}: ListTemplateProps<T>) {
  const hasSelection = selectable && selectedIds && selectedIds.size > 0;
  const appHeaderContext = useOptionalAppHeader();

  useEffect(() => {
    if (!useAppHeader || !appHeader || !appHeaderContext) return;
    appHeaderContext.setHeaderConfig(appHeader);
    return () => appHeaderContext.setHeaderConfig(null);
  }, [appHeader, appHeaderContext, useAppHeader]);

  const showInlineHeader = !(useAppHeader && appHeader && appHeaderContext);

  const handleItemClick = (item: T, index: number) => {
    if (selectable && onSelectionChange) {
      const itemKey = keyExtractor(item, index);
      const newSelection = new Set(selectedIds);

      if (newSelection.has(itemKey)) {
        newSelection.delete(itemKey);
      } else {
        newSelection.add(itemKey);
      }

      onSelectionChange(newSelection);
    }

    if (onItemClick) {
      onItemClick(item, index);
    }
  };

  const renderLoadingState = () => {
    if (loadingComponent) {
      return loadingComponent;
    }

    return (
      <div className="list-template__loading">
        <div className="list-template__spinner" />
        <p>Loading...</p>
      </div>
    );
  };

  const renderEmptyState = () => {
    if (emptyState) {
      return <div className="list-template__empty">{emptyState}</div>;
    }

    return (
      <div className="list-template__empty">
        <p className="list-template__empty-text content-wrap">No items found</p>
      </div>
    );
  };

  const renderErrorState = () => {
    return (
      <div className="list-template__error">
        <p className="list-template__error-text content-wrap">{error}</p>
      </div>
    );
  };

  return (
    <div className={`list-template list-template--${variant} ${className}`}>
      {/* Header */}
      {showInlineHeader && header && <div className="list-template__header">{header}</div>}

      {/* Search/Filter */}
      {search && <div className="list-template__search">{search}</div>}

      {/* Bulk Actions */}
      {hasSelection && bulkActions && (
        <div className="list-template__bulk-actions">
          <span className="list-template__selection-count">
            {selectedIds!.size} selected
          </span>
          {bulkActions}
        </div>
      )}

      {/* Content */}
      <div
        className={[
          'list-template__content',
          scrollable ? 'list-template__content--scrollable' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Loading State */}
        {loading && renderLoadingState()}

        {/* Error State */}
        {!loading && error && renderErrorState()}

        {/* Empty State */}
        {!loading && !error && items.length === 0 && renderEmptyState()}

        {/* List */}
        {!loading && !error && items.length > 0 && (
          <div className={`list-template__list ${listClassName}`} role="list">
            {items.map((item, index) => {
              const key = keyExtractor(item, index);
              const isSelected = selectedIds?.has(key);

              return (
                <div
                  key={key}
                  className={`list-template__item ${
                    selectable ? 'list-template__item--selectable' : ''
                  } ${isSelected ? 'list-template__item--selected' : ''}`}
                  onClick={() => handleItemClick(item, index)}
                  role="listitem"
                  aria-selected={selectable ? isSelected : undefined}
                >
                  {renderItem(item, index)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {footer && <div className="list-template__footer">{footer}</div>}
    </div>
  );
}

export default ListTemplate;
